import { colors, isChromiumBased, utils } from '@/helpers';
import { handleMediaUpload } from '@/pages/media-library/media.helpers';
import { create } from 'zustand';

type VoiceRecordingState = {
	savedRecording?: boolean;
	playing: boolean;
	recording: boolean;
	mediaRecorder: MediaRecorder | null;
	audioURL: string | null;
	blob: Blob | null;

	startTime: number;
	duration: number;

	audioContext: AudioContext | null;
	analyser: AnalyserNode | null;
	dataArray: Uint8Array | null;

	audioRef: HTMLAudioElement | null;
	audioVisualizerCanvasRef: HTMLCanvasElement | null;

	recordingAudioContext: AudioContext | null;
	playbackAudioContext: AudioContext | null;

	prevBarHeights?: number[];
};

type VoiceRecordingActions = {
	startRecording: () => Promise<void>;
	stopRecording: () => void;

	togglePlay: () => void;

	drawVisualizer: () => void;

	setVisualizerCanvas: (canvas: HTMLCanvasElement) => void;
	setVisualizerAudio: (audio: HTMLAudioElement) => void;

	setPlaying: (playing: boolean) => void;
	setNewAudio: (audio: { url: string; duration: number }) => void;

	handleSaveRecording: () => Promise<string | undefined>;
};

type VoiceRecordingStore = VoiceRecordingState & VoiceRecordingActions;

const initialState: VoiceRecordingState = {
	playing: false,
	recording: false,
	mediaRecorder: null,
	audioURL: null,
	blob: null,
	duration: 0,
	startTime: 0,
	audioContext: null,
	analyser: null,
	dataArray: null,
	audioVisualizerCanvasRef: null,
	audioRef: null,
	recordingAudioContext: null,
	playbackAudioContext: null,
};

const MAX_AUDIO_DURATION = 2 * 60 * 1000; // 2 minutes

// Audio visualizer configuration constants
const VISUALIZER_CONFIG = {
	// Smoothing settings (lower = smoother)
	SMOOTHING_FACTOR: 0.2,
	// Bar spacing in pixels
	BAR_SPACING: 1,
	// Minimum bar width in pixels
	MIN_BAR_WIDTH: 2,
	// Minimum height as percentage of canvas height (0-1)
	MIN_HEIGHT_RATIO: 0.05,
	// Safety margin at top to prevent cutoff (percentage of canvas height)
	TOP_MARGIN_RATIO: 0.05,
	// Value compression curve strength (lower = more boost to quiet sounds)
	CURVE_EXPONENT: 0.6,
	// Animation frame rate limiter (fps)
	FRAME_RATE: 90,
};

export const useVoiceRecordingStore = create<VoiceRecordingStore>((set, get) => ({
	...initialState,

	setNewAudio(audio: { url: string; duration: number }) {
		set({ audioURL: audio.url, duration: audio.duration });

		// If the duration is 0, we must try to get the duration from the audio file
		if (!audio.duration) {
			// Create media element using the audio URL
			const audioElement = new Audio(audio.url);

			// Get the duration of the audio
			audioElement.onloadedmetadata = () => {
				set({ duration: audioElement.duration });
			};
		}
	},

	setPlaying(playing: boolean) {
		set({ playing });
	},
	setVisualizerCanvas(canvas: HTMLCanvasElement) {
		set({ audioVisualizerCanvasRef: canvas });
	},

	setVisualizerAudio(audio: HTMLAudioElement) {
		try {
			// Create new AudioContext for playback if needed
			const context = new AudioContext();
			const analyser = context.createAnalyser();

			// Higher resolution for better visualization
			analyser.fftSize = 2048; // Doubled for even higher resolution
			analyser.smoothingTimeConstant = 0.9; // Increased for smoother transitions (was 0.8)

			const dataArray = new Uint8Array(analyser.frequencyBinCount);

			// Create source node from audio element
			const source = context.createMediaElementSource(audio);

			// Connect source → analyser → destination
			source.connect(analyser);
			analyser.connect(context.destination);

			// Save references
			set({
				playbackAudioContext: context,
				analyser,
				dataArray,
				audioRef: audio,
			});

			// Resume context (required in some browsers)
			context.resume();
		} catch (err) {
			console.error('Error connecting audio visualizer:', err);
		}
	},

	async startRecording() {
		try {
			// Temporoariy disable recording in chrome, tell them to use firefox
			if (isChromiumBased()) {
				utils?.toaster?.addToast?.({
					message: 'Recordings from chrome have temporarily been disabled while we work on a fix for silent recordings. Please use Firefox instead.',
					type: 'danger',
				});
				return;
			}

			// Clear the playback audio context
			if (get().playbackAudioContext) {
				set({ playbackAudioContext: null, playing: false });
			}

			const recordingAudioContext = new AudioContext();
			const analyser = recordingAudioContext.createAnalyser();
			const dataArray = new Uint8Array(analyser.frequencyBinCount);

			set({ recordingAudioContext, analyser, dataArray, duration: 0, savedRecording: false, audioURL: null });

			const permissions: PermissionStatus | undefined = await navigator.permissions.query({ name: 'microphone' as PermissionName });

			if (permissions?.state === 'denied') {
				utils?.toaster?.addToast?.({
					message: 'Microphone access denied by browser. Ensure you provide microphone access to your browser.',
					type: 'danger',
				});
				return;
			}

			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const source = recordingAudioContext.createMediaStreamSource(stream);
			source.connect(analyser);

			const recorder = new MediaRecorder(stream);
			const audioChunks: Blob[] = [];

			// On data available, push the data to the audioChunks array
			recorder.ondataavailable = (event) => {
				audioChunks.push(event.data);
			};

			// On stop, create a blob from the audioChunks and set the audioURL
			recorder.onstop = () => {
				const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
				const audioURL = URL.createObjectURL(audioBlob);

				// Get & set the duration of the audio
				const reader = new FileReader();
				reader.readAsArrayBuffer(audioBlob);

				// Decode the audio and get the duration
				reader.onload = (event) => {
					// Create a new audio context for decoding if needed
					const audioContext = new AudioContext();

					if (event.target?.result) {
						audioContext.decodeAudioData(
							event.target.result as ArrayBuffer,
							(buffer) => {
								// Successfully decoded - set the exact duration
								const audioDuration = buffer.duration;
								set({ duration: audioDuration });
							},
							(error) => {
								// Decoding failed - fallback to calculating duration from recording time
								console.error('Error decoding audio data:', error);
								const recordingDuration = (Date.now() - get().startTime) / 1000;
								set({ duration: recordingDuration });
							},
						);
					} else {
						// If reading failed, estimate from recording start time
						const recordingDuration = (Date.now() - get().startTime) / 1000;
						set({ duration: recordingDuration });
					}
				};

				// Handle file reading errors
				reader.onerror = () => {
					console.error('Error reading audio file');
					// Fallback to calculating duration from recording time
					const recordingDuration = (Date.now() - get().startTime) / 1000;
					set({ duration: recordingDuration });
				};

				set({ audioURL, blob: audioBlob });
			};

			// if recording hasn't stopped after MAX_AUDIO_DURATION, stop it
			setTimeout(() => {
				if (get().recording) {
					get().stopRecording();
				}
			}, MAX_AUDIO_DURATION);

			recorder.start();
			set({ recording: true, duration: 0, startTime: Date.now(), mediaRecorder: recorder });
			get().drawVisualizer();
		} catch {
			return;
		}
	},

	stopRecording() {
		// Stop the media recorder stream
		const tracks = get().mediaRecorder?.stream.getTracks();
		if (tracks) {
			for (const track of tracks) {
				track.stop();
			}
		}
		get().mediaRecorder?.stop();
		const { recordingAudioContext } = get();
		if (recordingAudioContext) {
			recordingAudioContext.close();
		}
		set({
			startTime: 0,
			recording: false,
			recordingAudioContext: null,
		});
	},

	togglePlay() {
		const { audioRef, playing } = get();
		if (audioRef) {
			if (playing) {
				audioRef.pause();
			} else {
				// Resume the audio context before playing
				const { playbackAudioContext } = get();
				playbackAudioContext?.resume().then(() => {
					audioRef.play();
					// Explicitly start visualization
					requestAnimationFrame(get().drawVisualizer);
				});
			}
		}
		set({ playing: !playing });
	},

	drawVisualizer() {
		const { audioVisualizerCanvasRef, analyser, dataArray, playing, recording } = get();

		// Only draw when actively recording or playing
		if (!audioVisualizerCanvasRef || (!playing && !recording) || !analyser || !dataArray) {
			return;
		}

		// Check if the canvas is actually visible in the document
		if (audioVisualizerCanvasRef.offsetParent === null) {
			// Canvas is not visible, don't waste resources drawing
			requestAnimationFrame(get().drawVisualizer);
			return;
		}

		// Use alpha: true to allow transparency
		const canvasCtx = audioVisualizerCanvasRef.getContext('2d');
		if (!canvasCtx) return;

		// Get proper dimensions
		const WIDTH = audioVisualizerCanvasRef.width;
		const HEIGHT = audioVisualizerCanvasRef.height;

		// Use the full width for bars - calculate how many bars will fit based on settings
		const { BAR_SPACING, MIN_BAR_WIDTH, FRAME_RATE } = VISUALIZER_CONFIG;

		// Calculate max number of bars that can fit in the width with the given spacing
		const maxBars = Math.floor(WIDTH / (MIN_BAR_WIDTH + BAR_SPACING));
		// Limit the number of bars to what we have data for
		const actualBars = Math.min(maxBars, 128); // Reduce resolution for better performance

		// Calculate exact bar width to fill the entire canvas
		const totalBarSpacing = (actualBars - 1) * BAR_SPACING;
		const barWidth = (WIDTH - totalBarSpacing) / actualBars;

		// Get configuration values
		const { SMOOTHING_FACTOR, MIN_HEIGHT_RATIO, TOP_MARGIN_RATIO, CURVE_EXPONENT } = VISUALIZER_CONFIG;

		// Height safety margin to prevent cutoff
		const heightSafetyMargin = HEIGHT * TOP_MARGIN_RATIO;
		const minHeight = HEIGHT * MIN_HEIGHT_RATIO;

		// Pre-compute common values outside the loop
		const availableHeight = HEIGHT - heightSafetyMargin;

		// Previous bar heights for additional smoothing
		const prevBarHeights = get().prevBarHeights || Array(actualBars).fill(0);
		const newBarHeights = Array(actualBars);

		// Get frequency data - use a smaller array size for better performance
		analyser.getByteFrequencyData(dataArray);

		// Properly clear the canvas between frames
		canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);

		// Cache for colors - reuse gradients for similar heights
		const gradientCache = new Map();

		// Find max value for better scaling
		let maxValue = 1; // Avoid division by zero
		for (let i = 0; i < dataArray.length && i < actualBars * 4; i += 4) {
			maxValue = Math.max(maxValue, dataArray[i]);
		}

		// Sample step for the data array - just use incremental samples for better performance
		const sampleStep = Math.ceil(dataArray.length / actualBars);

		// Pastel color palette
		const pastelColors = [
			{ light: 'hsl(210, 70%, 80%)', dark: 'hsl(210, 80%, 70%)' }, // Pastel blue
			{ light: 'hsl(180, 70%, 80%)', dark: 'hsl(180, 80%, 70%)' }, // Pastel teal
			{ light: 'hsl(150, 70%, 80%)', dark: 'hsl(150, 80%, 70%)' }, // Pastel green
			{ light: 'hsl(120, 70%, 80%)', dark: 'hsl(120, 80%, 70%)' }, // Pastel lime
			{ light: 'hsl(60, 70%, 80%)', dark: 'hsl(60, 80%, 70%)' }, // Pastel yellow
			{ light: 'hsl(35, 70%, 80%)', dark: 'hsl(35, 80%, 70%)' }, // Pastel orange
			{ light: 'hsl(0, 70%, 80%)', dark: 'hsl(0, 80%, 70%)' }, // Pastel red
			{ light: 'hsl(330, 70%, 80%)', dark: 'hsl(330, 80%, 70%)' }, // Pastel pink
			{ light: 'hsl(280, 70%, 80%)', dark: 'hsl(280, 80%, 70%)' }, // Pastel purple
		];

		// Draw bars in a batch for better performance
		let x = 0; // Start from left edge
		for (let barIndex = 0; barIndex < actualBars; barIndex++) {
			// Get sample index for this bar
			const sampleIndex = barIndex * sampleStep;
			const value = sampleIndex < dataArray.length ? dataArray[sampleIndex] : 0;

			// Apply non-linear scaling with fast power approximation
			const normalizedValue = value / 255;

			// Use a simpler calculation for height
			const adjustedValue = normalizedValue ** CURVE_EXPONENT;
			const rawHeight = minHeight + adjustedValue * (availableHeight - minHeight);

			// Apply smoother transition from previous frame
			const barHeight = Math.min(prevBarHeights[barIndex] * (1 - SMOOTHING_FACTOR) + rawHeight * SMOOTHING_FACTOR, HEIGHT - 5);

			newBarHeights[barIndex] = barHeight;

			// Determine color based on height for fewer calculations
			const relativeIntensity = value / (maxValue || 255);
			const colorIndex = Math.min(Math.floor(relativeIntensity * 5), pastelColors.length - 1);
			const { light, dark } = pastelColors[colorIndex];

			// Use rounded height for gradient caching (less memory usage with fewer unique gradients)
			const roundedHeight = Math.round(barHeight / 2) * 2;
			const gradientKey = `${roundedHeight}-${colorIndex}`;

			// Reuse gradients when possible for improved performance
			let gradient = gradientCache.get(gradientKey);
			if (!gradient) {
				gradient = canvasCtx.createLinearGradient(0, HEIGHT, 0, HEIGHT - roundedHeight);
				gradient.addColorStop(0, dark);
				gradient.addColorStop(1, light);

				// Only cache a reasonable number of gradients
				if (gradientCache.size < 30) {
					gradientCache.set(gradientKey, gradient);
				}
			}

			// Draw the bar
			canvasCtx.fillStyle = gradient;
			canvasCtx.beginPath();

			// Use simpler drawing for better performance (just rounded top corners)
			const y = HEIGHT - barHeight;
			const radius = 4;

			// Simplified rounded rectangle drawing with fewer operations
			canvasCtx.moveTo(x, HEIGHT);
			canvasCtx.lineTo(x, y + radius);
			canvasCtx.quadraticCurveTo(x, y, x + radius, y);
			canvasCtx.lineTo(x + barWidth - radius, y);
			canvasCtx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
			canvasCtx.lineTo(x + barWidth, HEIGHT);
			canvasCtx.fill();

			// Move to next bar position
			x += barWidth + BAR_SPACING;
		}

		// Store bar heights for next frame
		set({ prevBarHeights: newBarHeights });

		// Use requestAnimationFrame directly for better performance
		// Only add the setTimeout delay if we're specifically trying to limit frame rate
		if (FRAME_RATE < 60) {
			setTimeout(() => {
				requestAnimationFrame(get().drawVisualizer);
			}, 1000 / FRAME_RATE);
		} else {
			requestAnimationFrame(get().drawVisualizer);
		}
	},

	async handleSaveRecording() {
		const { blob } = get();

		if (!blob) {
			utils?.toaster?.addToast?.({
				message: 'No audio blob found',
				type: 'danger',
			});
			return '';
		}

		// Create a File to upload using the Blob
		const file = new File([blob], 'audio.wav', { type: 'audio/wav' });
		const { response, valid } = await handleMediaUpload(file, {}, { type: 'audio', maxDuration: 120, maxMB: 5 });
		if (!valid) {
			return '';
		}
		const [audio] = response || [];

		set({ savedRecording: true, audioURL: audio.url });

		return audio.url;
	},
}));
